/**
 * AppRouter — Progressive React Router wrapper
 *
 * This file introduces proper `<Routes>/<Route>` routing for new enterprise
 * pages while preserving the existing App.tsx state-based page rendering.
 *
 * Strategy:
 * - New pages (teams, org admin) get proper `<Route>` entries
 * - The existing App component acts as a catch-all for the workspace
 *   and legacy pages that still use boolean flags (terms, privacy, etc.)
 * - Over time, individual pages can be extracted from App.tsx into
 *   standalone route components without breaking anything.
 *
 * This file replaces the direct `<App />` render in index.tsx.
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Eagerly-loaded core app (canvas + all legacy routes)
import App from './App';
import { LoadingScreen } from './components/GlassOrb';

// Where Claude's connector sends the person to allow it (loaded only for that page)
const ConnectClaudePage = React.lazy(() => import('./pages/ConnectClaudePage'));

// ── Loading fallback ─────────────────────────────────────────────────────────

function PageLoader() {
  return <LoadingScreen title="Loading" className="min-h-screen" />;
}

// ── Router ───────────────────────────────────────────────────────────────────

export default function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* 
          Enterprise routes — these are cleanly separated from the canvas.
          They render standalone pages with their own layout.
        */}
        {/* Future: <Route path="/teams" element={<TeamManagementPage />} /> */}
        {/* Future: <Route path="/org/:orgId" element={<OrgDashboard />} /> */}
        {/* Future: <Route path="/invite/:token" element={<InviteAcceptPage />} /> */}
        <Route path="/oauth/authorize" element={<ConnectClaudePage />} />

        {/* 
          Catch-all — the existing App handles:
          /, /workspaces, /settings, /subscription, /analytics,
          /terms, /privacy, /about, and unknown-route 404.
          
          As pages are extracted from App.tsx, they can be moved to
          explicit <Route> entries above this catch-all.
        */}
        <Route path="/*" element={<App />} />
      </Routes>
    </Suspense>
  );
}
